/**
 * RoleAndPermissionForm.jsx
 * Create → /roles-and-permissions/create
 * Edit   → /roles-and-permissions/:id/edit
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Save, ChevronDown, ChevronRight, Shield,
  CheckSquare, Square, EyeOff, Eye, ArrowLeft,
} from "lucide-react";
import { toast } from "react-toastify";
import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import PageLoader from "../../helpers/PageLoader";
import {
  PERMISSION_MODULES, ACTIONS,
  buildInitialPermissions, emptyPerms, fullPerms,
} from "../../config/permissionModules";

const ACTION_LABELS = { view: "View", add: "Add", edit: "Edit", delete: "Delete", export: "Export" };

const isAllGranted    = (p) => ACTIONS.every((a) => p?.[a] === true) && !p?.hide;
const isAnyGranted    = (p) => ACTIONS.some((a) => p?.[a] === true);
const isModuleFull    = (perms, mod) =>
  [mod.key, ...(mod.submodules || []).map((s) => s.key)].every((k) => isAllGranted(perms[k]));
const isModulePartial = (perms, mod) =>
  [mod.key, ...(mod.submodules || []).map((s) => s.key)].some((k) => isAnyGranted(perms[k])) &&
  !isModuleFull(perms, mod);

export default function RoleAndPermissionForm() {
  const navigate  = useNavigate();
  const { portalName, id }    = useParams();
  const isEdit    = Boolean(id);

  const [roleName,    setRoleName]    = useState("");
  const [description, setDescription] = useState("");
  const [isDefault,   setIsDefault]   = useState(false);
  const [permissions, setPermissions] = useState(buildInitialPermissions);
  const [expanded,    setExpanded]    = useState(() =>
    Object.fromEntries(PERMISSION_MODULES.slice(0, 3).map((m) => [m.key, true]))
  );
  const [errors,  setErrors]  = useState({});
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);

  /* ── fetch ─────────────────────────────────────────────────────────────── */
  const fetchRole = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(SummaryApi.getRoleById.url(id));
      const role = data.data;
      setRoleName(role.roleName || "");
      setDescription(role.description || "");
      setIsDefault(role.isDefault || false);

      const merged = buildInitialPermissions();
      Object.keys(role.permissions || {}).forEach((key) => {
        if (merged[key]) {
          const s = role.permissions[key];
          merged[key] = {
            view: !!s.view, add: !!s.add, edit: !!s.edit,
            delete: !!s.delete, export: !!s.export, hide: !!s.hide,
          };
        }
      });
      setPermissions(merged);
    } catch {
      toast.error("Failed to load role");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchRole(); }, [fetchRole]);

  /* ── mutators ──────────────────────────────────────────────────────────── */
  const toggleAction = (key, action, value) =>
    setPermissions((prev) => {
      const cur = { ...prev[key] };
      if (action === "hide")
        return { ...prev, [key]: value ? { ...emptyPerms(), hide: true } : { ...cur, hide: false } };
      return { ...prev, [key]: { ...cur, hide: false, [action]: value } };
    });

  const setKeyAll    = (key, grant) =>
    setPermissions((prev) => ({ ...prev, [key]: grant ? fullPerms() : emptyPerms() }));

  const setModuleAll = (mod, grant) => {
    const keys = [mod.key, ...(mod.submodules || []).map((s) => s.key)];
    setPermissions((prev) => {
      const next = { ...prev };
      keys.forEach((k) => { next[k] = grant ? fullPerms() : emptyPerms(); });
      return next;
    });
  };

  const setGlobalAll = (grant) => {
    const next = {};
    Object.keys(permissions).forEach((k) => { next[k] = grant ? fullPerms() : emptyPerms(); });
    setPermissions(next);
  };

  const isGlobalAll  = Object.values(permissions).every(isAllGranted);
  const toggleExpand = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));
  const expandAll    = () => setExpanded(Object.fromEntries(PERMISSION_MODULES.map((m) => [m.key, true])));
  const collapseAll  = () => setExpanded({});

  /* ── submit ────────────────────────────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!roleName.trim()) {
      setErrors({ roleName: "Role name is required" });
      toast.error("Please enter a role name");
      return;
    }
    setSaving(true);
    try {
      const payload = { roleName: roleName.trim(), description, isDefault, permissions };
      isEdit
        ? await api.put(SummaryApi.updateRole.url(id), payload)
        : await api.post(SummaryApi.createRole.url, payload);
      toast.success(`Role ${isEdit ? "updated" : "created"} successfully`);
      navigate(`/${portalName}/security/roles-and-permissions`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save role");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageLoader />;

  /* ── render ────────────────────────────────────────────────────────────── */
  return (
    <div className="p-2">

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={() => navigate(-1)}
          className="rounded-lg bg-red-50 border border-red-100 hover:border-primary text-textPrimary hover:text-primary p-2 transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <div className="p-1.5">
            <Shield className="w-5 h-5 text-red-600" />
          </div>
          <h1 className="text-lg sm:text-xl font-bold text-gray-800">
            {isEdit ? "Edit Role & Permissions" : "Create New Role"}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* Role Details Card */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-4 bg-red-600 rounded-full" />
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Role Details</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text" value={roleName}
                onChange={(e) => { setRoleName(e.target.value); setErrors({}); }}
                placeholder="e.g. Sales Manager"
                className={`w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-300 transition ${
                  errors.roleName ? "border-red-500 bg-red-50" : "border-gray-300"
                }`}
              />
              {errors.roleName && <p className="text-red-500 text-xs mt-1">{errors.roleName}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text" value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this role"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-300 transition"
              />
            </div>

            <div className="sm:col-span-2 flex items-center gap-3 pt-1">
              <button
                type="button" onClick={() => setIsDefault((v) => !v)}
                className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-red-400 ${
                  isDefault ? "bg-red-600" : "bg-gray-200"
                }`}
              >
                <span className={`inline-block h-4 w-4 mt-1 transform rounded-full bg-white shadow transition-transform ${
                  isDefault ? "translate-x-6" : "translate-x-1"
                }`} />
              </button>
              <div>
                <p className="text-sm font-medium text-gray-700">Set as Default Role</p>
                <p className="text-xs text-gray-400">Auto-assigned when inviting new users</p>
              </div>
            </div>
          </div>
        </div>

        {/* Permission Matrix */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">

          {/* Matrix toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 sm:px-5 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <div className="w-1 h-4 bg-red-600 rounded-full" />
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Permission Matrix</h2>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <button type="button" onClick={() => setGlobalAll(!isGlobalAll)}
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                  isGlobalAll
                    ? "bg-red-600 text-white border-red-600 hover:bg-red-700"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-red-50 hover:border-red-300 hover:text-red-600"
                }`}>
                {isGlobalAll ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                {isGlobalAll ? "Deselect All" : "Select All"}
              </button>
              <button type="button" onClick={expandAll}  className="text-xs text-red-600 hover:underline font-medium">Expand all</button>
              <button type="button" onClick={collapseAll} className="text-xs text-gray-400 hover:underline">Collapse</button>
            </div>
          </div>

          {/* Horizontal scroll wrapper for mobile */}
          <div className="overflow-x-auto">
            <div className="min-w-[620px]">

              {/* Column headers */}
              <div className="grid grid-cols-[1fr_repeat(5,54px)_46px] gap-x-1 px-4 sm:px-5 py-2.5 bg-gray-50 border-b border-gray-200">
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Module / Submodule</span>
                {ACTIONS.map((a) => (
                  <span key={a} className="text-[10px] font-bold text-gray-500 uppercase tracking-wider text-center">
                    {ACTION_LABELS[a]}
                  </span>
                ))}
                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider text-center">Hide</span>
              </div>

              {/* Rows */}
              <div className="divide-y divide-gray-100">
                {PERMISSION_MODULES.map((mod) => {
                  const isOpen      = expanded[mod.key];
                  const modPerms    = permissions[mod.key] || emptyPerms();
                  const allGranted  = isModuleFull(permissions, mod);
                  const partGranted = isModulePartial(permissions, mod);
                  const isHidden    = modPerms.hide;

                  return (
                    <div key={mod.key}>
                      {/* Module row */}
                      <div className={`grid grid-cols-[1fr_repeat(5,54px)_46px] gap-x-1 items-center px-4 sm:px-5 py-3 transition-colors
                        ${isHidden ? "bg-gray-50 opacity-70" : "hover:bg-red-50"}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <button type="button" onClick={() => toggleExpand(mod.key)}
                            className="shrink-0 text-gray-400 hover:text-red-500 transition-colors">
                            {(mod.submodules?.length || 0) > 0
                              ? isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
                              : <span className="w-4 h-4 block" />}
                          </button>
                          <button type="button" onClick={() => setModuleAll(mod, !allGranted)}
                            className={`shrink-0 transition-colors ${
                              allGranted ? "text-red-600" : partGranted ? "text-red-300" : "text-gray-300 hover:text-gray-500"
                            }`}>
                            {allGranted ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                          </button>
                          <span className="font-semibold text-sm text-gray-800 truncate">{mod.label}</span>
                          {isHidden && <span className="shrink-0 text-[10px] bg-red-100 text-red-500 font-semibold px-1.5 py-0.5 rounded">HIDDEN</span>}
                        </div>

                        {ACTIONS.map((action) => (
                          <div key={action} className="flex justify-center">
                            <input type="checkbox"
                              checked={modPerms[action] || false}
                              disabled={isHidden}
                              onChange={(e) => toggleAction(mod.key, action, e.target.checked)}
                              className="w-4 h-4 accent-red-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                            />
                          </div>
                        ))}

                        <div className="flex justify-center">
                          <button type="button" onClick={() => toggleAction(mod.key, "hide", !isHidden)}
                            title={isHidden ? "Restore visibility" : "Hide from sidebar"}
                            className={`p-1 rounded transition-colors ${
                              isHidden ? "text-red-500 hover:text-red-700 bg-red-50" : "text-gray-300 hover:text-gray-500"
                            }`}>
                            {isHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      {/* Submodule rows */}
                      {isOpen && (mod.submodules || []).map((sub) => {
                        const subPerms = permissions[sub.key] || emptyPerms();
                        const subAll   = isAllGranted(subPerms);
                        const subHide  = subPerms.hide;
                        return (
                          <div key={sub.key}
                            className={`grid grid-cols-[1fr_repeat(5,54px)_46px] gap-x-1 items-center pl-10 sm:pl-14 pr-4 sm:pr-5 py-2.5 border-t border-gray-100 transition-colors
                              ${subHide ? "bg-gray-50 opacity-70" : "bg-slate-50/60 hover:bg-red-50"}`}>
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="w-3 h-px bg-gray-300 shrink-0" />
                              <button type="button" onClick={() => setKeyAll(sub.key, !subAll)}
                                className={`shrink-0 transition-colors ${subAll ? "text-red-500" : "text-gray-300 hover:text-gray-500"}`}>
                                {subAll ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                              </button>
                              <span className="text-sm text-gray-700 truncate">{sub.label}</span>
                              {subHide && <span className="shrink-0 text-[10px] bg-red-100 text-red-500 font-semibold px-1.5 py-0.5 rounded">HIDDEN</span>}
                            </div>
                            {ACTIONS.map((action) => (
                              <div key={action} className="flex justify-center">
                                <input type="checkbox"
                                  checked={subPerms[action] || false}
                                  disabled={subHide}
                                  onChange={(e) => toggleAction(sub.key, action, e.target.checked)}
                                  className="w-4 h-4 accent-red-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                                />
                              </div>
                            ))}
                            <div className="flex justify-center">
                              <button type="button" onClick={() => toggleAction(sub.key, "hide", !subHide)}
                                title={subHide ? "Restore" : "Hide"}
                                className={`p-1 rounded transition-colors ${
                                  subHide ? "text-red-500 hover:text-red-700 bg-red-50" : "text-gray-300 hover:text-gray-500"
                                }`}>
                                {subHide ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 justify-end pb-10">
          <button type="button" onClick={() => navigate(`/${portalName}/security/roles-and-permissions`)}
            disabled={saving}
            className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button type="submit" disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 shadow-sm">
            <Save className="w-4 h-4" />
            {saving ? "Saving…" : isEdit ? "Update Role" : "Create Role"}
          </button>
        </div>
      </form>
    </div>
  );
}